(ns machtfit.startpage.events
  (:require [cljs-time.coerce :as time.coerce]
            clojure.data
            [goog.events :as events]
            [machtfit.api :as api]
            [machtfit.app.routes :as routes]
            [machtfit.app.tracking :as tracking]
            [machtfit.repository.search-facets :as search-facets]
            [machtfit.startpage.api-events :as api-events]
            [machtfit.startpage.emarsys :as emarsys]
            [machtfit.startpage.filter :as filter]
            [machtfit.startpage.layout :refer [reset-ui]]
            [machtfit.startpage.products-cache :as products-cache]
            [machtfit.startpage.search :as search]
            [machtfit.startpage.statics :as statics]
            [machtfit.startpage.subs :as-alias subs]
            [machtfit.stepchall.alert-bar :as alert-bar]
            [machtfit.stepchall.tracker :as tracker]
            [machtfit.storage :as-alias storage]
            [machtfit.url-params :as url-params]
            [re-frame.core :as rf]
            [vimsical.re-frame.fx.track :as fx.track])
  (:import [goog.events EventType]))

(defn- locations-equal? [location-1 location-2]
  (= (:coordinates location-1)
     (:coordinates location-2)))

(defn- location->posthog-properties [db {:keys [address] :as location}]
  (if-not location
    {}
    (let [home-location (get-in db [:user :home-location])
          work-location (get-in db [:user :place-of-business-location])
          cleaned-address (-> address
                              (select-keys [:city
                                            :postcode])
                              (assoc :type
                                     (cond
                                       (locations-equal? location work-location) "work"
                                       (locations-equal? location home-location) "home"
                                       :else "other")))]
      cleaned-address)))

(rf/reg-event-fx ::filter-changed-debounced
  (fn [{:keys [db]} [_ {:keys [db-snapshot
                               products-count
                               filter-preset
                               location
                               effective-filter
                               search-engine]}]]
    (let [current-user-filter (get db :filter)
          previous-user-filter (get-in db [:tracking :previous-user-filter])
          [added removed] (clojure.data/diff current-user-filter previous-user-filter)]
      {:db (update db :tracking dissoc :last-filter-action)
       ::tracking/capture-event [db "offers_filtered"
                                 {"matched offers count" products-count
                                  "filter-preset" filter-preset
                                  "search-engine" search-engine
                                  "action" (get-in db-snapshot [:tracking :last-filter-action])
                                  "user-filter" {"current" current-user-filter
                                                 "previous" previous-user-filter
                                                 "added" added
                                                 "removed" removed}
                                  "filter" effective-filter
                                  "location" (location->posthog-properties db-snapshot location)}]})))

(rf/reg-event-fx ::filter-changed
  (fn [{:keys [db]} [_ {:keys [page-id products-settled? effective-filter]
                        :as data-for-tracking}]]
    (when (and (= page-id :catalog)
               products-settled?)
      {:fx [[::tracking/assoc-in-super-properties [[:catalog :filter] effective-filter]]
            [::tracking/assoc-in-super-properties [[:catalog :user_filter] (get db :filter)]]
            [:dispatch-debounce {:key :track-filter-changed
                                 :event [::filter-changed-debounced
                                         (assoc data-for-tracking :db-snapshot db)]
                                 :delay 100}]]})))

(defn merge-cljs-data [db js-data]
  (-> db
      (merge js-data)
      (update-in [:user :labels] set)
      (update-in [:user :date-joined] time.coerce/from-string)
      (update-in [:user :favorites] set)))

(rf/reg-event-fx ::track-map-status
  (fn [_ [_ show-map?]]
    {::tracking/deep-merge-super-properties {:catalog {:view (if show-map? "map" "list")}}}))

(def tracks [{:id :filter-changed
              :subscription [::subs/filter-data-for-tracking]
              :event-fn (fn [data-for-tracking] [::filter-changed data-for-tracking])}
             {:id :map-shown-or-hidden
              :subscription [::subs/show-map?]
              :event-fn (fn [show-map?] [::track-map-status show-map?])}])

(rf/reg-event-fx ::register-tracks
  (fn [{:keys [db]} _]
    (when (:is-spa? db)
      {::fx.track/register tracks})))

(rf/reg-event-fx ::re-register-tracks
  (fn [{:keys [db]} _]
    (when (:is-spa? db)
      {:fx [[::fx.track/dispose tracks]
            [::fx.track/register tracks]]})))

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(defn ^:dev/after-load re-register-tracks []
  (rf/dispatch [::re-register-tracks]))

(rf/reg-event-fx ::init [(rf/inject-cofx ::storage/local-storage-fetch)]
  (fn [{:keys [db local-storage]} [_ js-data]]
    {:db
     (-> db
         (assoc :is-spa? true)
         (assoc :map-toggle? false)
         (merge {:ui {:show-products 28}})
         (merge-cljs-data js-data)
         (assoc :fetch-emarsys-campaigns? (not (get-in local-storage ['klaro :emarsys])))
         (assoc :products-location
                (or (get local-storage 'machtfit.app.startpage.products-location)
                    (get-in js-data [:user :home-location])
                    (get-in js-data [:user :place-of-business-location])))
         (products-cache/restore-from-session-storage local-storage)
         (alert-bar/restore-from-local-storage local-storage)
         (tracker/restore-from-local-storage local-storage)
         (emarsys/restore-from-local-storage local-storage))
     :dispatch [::register-tracks]}))

(rf/reg-fx ::reload
  (fn [_]
    (.reload js/window.location true)))

(rf/reg-event-fx ::reload
  (fn [_ _]
    {::reload nil
     ::storage/local-storage-clear nil}))

(rf/reg-event-fx ::window-focused
  (fn [{:keys [db]} _]
    (api-events/load-data-on-demand {:db db})))

(rf/reg-event-db ::browser-before-unload
  (fn [db _]
    (assoc db :unloading? true)))

(rf/reg-event-fx ::navigate-to-page
  (fn [{:keys [db]} [_ page]]
    (-> {:db db}
        (assoc-in [:db :ui :page] page)
        (update :db reset-ui)
        (api-events/load-data-on-demand))))

(rf/reg-event-fx ::navigate-to-catalog
  (fn [{:keys [db]} [_ filter-preset {:keys [show-map] :as query-params}]]
    (let [non-empty-categories (filter/db->non-empty-categories db)
          machtfit-area-only-online? (get-in db [:user :machtfit-area-only-online?])
          user-labels (-> db :user :labels)
          filter-from-params (url-params/query-params->filter query-params)
          filter-event #(merge
                         (filter/filter-preset->filter filter-preset non-empty-categories user-labels)
                         filter-from-params)
          show-map (if (and show-map (not machtfit-area-only-online?))
                     (url-params/url-param->boolean show-map)
                     (:map-toggle? db))]
      (-> {:db db
           :fx (cond-> []
                 ;; The facets decide which of a preset's categories survive
                 ;; ``cleaned-filter-presets``, and they land a round trip after
                 ;; the request below is built, so the request is re-asked for
                 ;; once they have. ``::fetch`` dedupes an unchanged one.
                 (search/server-only? db)
                 (conj [:dispatch [::search-facets/fetch
                                   [::search/fetch (:keyword filter-from-params)]]])
                 :always (conj [:dispatch [::search/fetch (:keyword filter-from-params)]]))}
          (assoc-in [:db :map-toggle?] show-map)
          (assoc-in [:db :ui :page] {:id :catalog})
          (update :db filter/apply-filter-event filter-event filter-preset)
          (update :db reset-ui)
          (api-events/load-data-on-demand)))))

(defn- close-all-dropdowns [db]
  (-> db
      (assoc-in [:ui :appointments-product-id] nil)
      (assoc-in [:ui :visible-dropdown] nil)
      (assoc-in [:ui :user-menu-open?] false)))

(rf/reg-event-db ::overlay-clicked
  (fn [db _]
    (let [appointments-product-id (get-in db [:ui :appointments-product-id])
          visible-dropdown (get-in db [:ui :visible-dropdown])
          user-menu-open? (get-in db [:ui :user-menu-open?])]
      (when (or appointments-product-id (some? visible-dropdown) user-menu-open?)
        (close-all-dropdowns db)))))

(rf/reg-event-db ::esc-pressed
  (fn [db _]
    (close-all-dropdowns db)))

(rf/reg-event-fx ::close-notification-clicked
  (fn [{:keys [db]} [_ notification-id]]
    (let [close-notification (api/request db
                                          {:data `[(:notification-acknowledged
                                                    {:notification_id ~notification-id}
                                                    :notification)]})]
      {:post close-notification
       :db (update-in db [:user :notifications] #(drop 1 %))})))

(rf/reg-event-fx ::close-survey-notification-clicked
  (fn [{:keys [db]} [_ id display-count]]
    (let [close-survey-notification (api/request db
                                                 {:data `[(:survey-closed
                                                           {:id ~id}
                                                           :survey)]})]
      {:post close-survey-notification
       ::tracking/capture-ui-event [db
                                    "survey_notification"
                                    "closes_onsite_feedback_survey_promo_q2_2026"
                                    {:display_count display-count
                                     :type "closed"}]
       :db (assoc-in db [:user :survey] nil)})))

(rf/reg-fx ::change-url
  (fn [url]
    (routes/change-url url)))

(rf/reg-event-db ::toggle-filter-mobile-dropdown
  (fn [db [_ filter]]
    (update-in db [:ui :visible-mobile-dropdown] #(cond
                                                    (= % filter) nil
                                                    :else filter))))

(rf/reg-event-db ::toggle-filter-dropdown
  (fn [db [_ filter]]
    (update-in db [:ui :visible-dropdown] #(cond
                                             (= % filter) nil
                                             :else filter))))

(defn update-filter
  ([db filter-event]
   (update-filter db filter-event (-> db :ui :filter-preset)))
  ([db filter-event new-filter-preset]
   (let [new-db (filter/apply-filter-event db filter-event new-filter-preset)
         new-filter (:filter new-db)]
     {:dispatch (routes/route-and-navigate-to-catalog new-filter)})))

(rf/reg-event-db ::set-filter-action
  (fn [db [_ action]]
    (assoc-in db [:tracking :last-filter-action] action)))

(defn act-on-filter [db filter-event action]
  (-> (update-filter db filter-event)
      (assoc :db (assoc-in db [:tracking :last-filter-action] action))))

(defn update-filter-into-unspecific-preset [db filter-event]
  (let [effective-filter (filter/db->effective-filter
                          db (filter/db->non-empty-categories db))
        db* (filter/apply-filter-event db (constantly (filter-event effective-filter)) nil)
        new-filter (:filter db*)]
    {:dispatch (routes/route-and-navigate-to-catalog new-filter)}))

(rf/reg-event-fx ::set-filter-false
  (fn [{:keys [db]} [_event filter-key]]
    (update-filter db #(assoc % filter-key false))))

(rf/reg-event-fx ::remove-filter
  (fn [{:keys [db]} [_event filter-key]]
    (update-filter db #(dissoc % filter-key))))

(rf/reg-event-fx ::remove-filter-element
  (fn [{:keys [db]} [_event filter-key value]]
    (update-filter db #(update % filter-key disj value))))

(defn- current-area [db]
  (:area (filter/db->effective-filter db (filter/db->non-empty-categories db))))

(rf/reg-event-fx ::reset-filter-clicked
  (fn [{:keys [db]} _]
    (update-filter db (constantly (statics/area->base-filter (current-area db))))))

(rf/reg-event-fx ::checkbox-clicked
  (fn [{:keys [db]} [_ key filter-key checked? dropdown-key]]
    (act-on-filter db #(update % filter-key
                               (fn [current]
                                 (if checked?
                                   (conj current key)
                                   (disj current key))))
                   {:type "filter_bar"
                    :details {:section dropdown-key
                              :filter_key filter-key
                              :value key
                              :was_added checked?}})))

(rf/reg-event-fx ::filter-distance-changed
  (fn [{:keys [db]} [_ distance action]]
    (act-on-filter db
                   #(assoc % :distance distance)
                   (update action
                           :details merge {:filter-key :distance
                                           :value distance
                                           :was_added (not= distance 70)}))))

(rf/reg-event-fx ::filter-price-range-changed
  (fn [{:keys [db]} [_ price-range]]
    (update-filter db #(assoc %
                              :price-min (:min price-range)
                              :price-max (:max price-range)))))

(rf/reg-event-db ::load-more-clicked
  (fn [db _]
    (update-in db [:ui :show-products] + 30)))

(rf/reg-event-db ::show-online-clicked
  (fn [db _]
    (update-filter db #(assoc % :online true))))

(rf/reg-event-fx ::sort-by-clicked
  (fn [{:keys [db]} [_ value]]
    (act-on-filter db
                   #(assoc % :sort-by value)
                   {:type "filter_bar"
                    :details {:filter_key :sort-by
                              :value value
                              :is_reset (= value :recommended)}})))

(rf/reg-event-fx ::keyword-filter-changed
  (fn [_ [_ value]]
    {:dispatch-debounce {:key :keyword-change-request
                         :event [::keyword-filter-after-debounce value]
                         :delay 250}}))

(rf/reg-event-fx ::keyword-filter-after-debounce
  (fn [{:keys [db]} [_ value]]
    (update-filter db #(assoc % :keyword value))))

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(defonce focus-event-id
  (events/listen js/window EventType.FOCUS #(rf/dispatch [::window-focused])))

;; trigger routing when using the browser back and forward buttons to navigate through history
#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(defonce popstate-event-id
  (events/listen js/window EventType.POPSTATE #(rf/dispatch-sync [::routes/routing routes/router])))

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(defonce beforeunload-event-id
  (events/listen js/window EventType.BEFOREUNLOAD #(rf/dispatch [::browser-before-unload])))

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(defonce keyup-event-id
  (events/listen js/document EventType.KEYUP (fn [event]
                                               (when (= 1 "Escape" (.-key event))
                                                 (rf/dispatch [::esc-pressed])))))
